<script setup lang="ts">
import { defineProps, ref, watch, reactive, provide, computed } from "vue";
import {
  DestinyMatrix,
  DestinyMatrixKeysEnum,
  PREMIUM_KEYS_SET,
} from "@/types/destinyMatrix";
import { getMatrixDescription } from "@/api/destinyMatrix";
import EmptyModal from "@/components/modals/EmptyModal.vue";
import PersonalDataAccordion from "@/views/Calculators/DestinyMatrix/components/accordions/PersonalDataAccordion.vue";
import FamilyPowerAccordion from "@/views/Calculators/DestinyMatrix/components/accordions/FamilyPowerAccordion.vue";
import PointOfSpiritComfortAccordion from "@/views/Calculators/DestinyMatrix/components/accordions/PointOfSpiritComfortAccordion.vue";
import SelfRealizationAccordion from "@/views/Calculators/DestinyMatrix/components/accordions/SelfRealizationAccordion.vue";
import VacationAccordion from "@/views/Calculators/DestinyMatrix/components/accordions/VacationAccordion.vue";
import AlcoholProneAccordion from "@/views/Calculators/DestinyMatrix/components/accordions/AlcoholProneAccordion.vue";
import KarmaAndFortyYearsTasksAccordion from "@/views/Calculators/DestinyMatrix/components/accordions/KarmaAndFortyYearsTasksAccordion.vue";
import PurposesAccordion from "@/views/Calculators/DestinyMatrix/components/accordions/PurposesAccordion.vue";
import SpiritKarmaAccordion from "@/views/Calculators/DestinyMatrix/components/accordions/SpiritKarmaAccordion.vue";
import ChildParentsKarmaAccordion from "@/views/Calculators/DestinyMatrix/components/accordions/ChildParentsKarmaAccordion.vue";
import FamilyTasksSevenGenerationAccordion from "@/views/Calculators/DestinyMatrix/components/accordions/FamilyTasksSevenGenerationAccordion.vue";
import ExcessWeightAccordion from "@/views/Calculators/DestinyMatrix/components/accordions/ExcessWeightAccordion.vue";
import GettingReachByBirthYearAccordion from "@/views/Calculators/DestinyMatrix/components/accordions/GettingReachByBirthYearAccordion.vue";
import MoneyInMatrixAccordion from "@/views/Calculators/DestinyMatrix/components/accordions/MoneyInMatrixAccordion.vue";
import PersonalPointOfPowerAccordion from "@/views/Calculators/DestinyMatrix/components/accordions/PersonalPointOfPowerAccordion.vue";
import PrevLifesTasksAccordion from "@/views/Calculators/DestinyMatrix/components/accordions/PrevLifesTasksAccordion.vue";
import RelationsInMatrixAccordion from "@/views/Calculators/DestinyMatrix/components/accordions/RelationsInMatrixAccordion.vue";
import WhatGivesYouResourceAccordion from "@/views/Calculators/DestinyMatrix/components/accordions/WhatGivesYouResourceAccordion.vue";
import WhomWorksForSoulAccordion from "@/views/Calculators/DestinyMatrix/components/accordions/WhomWorksForSoulAccordion.vue";
import RequirePremiumModal from "@/views/Calculators/DestinyMatrix/components/RequirePremiumModal.vue";
import HealthMapAccordion from "@/views/Calculators/DestinyMatrix/components/accordions/HealthMapAccordion.vue";
import ProgramsAccordion from "@/views/Calculators/DestinyMatrix/components/accordions/ProgramsAccordion.vue";
import YearTasksAccordion from "@/views/Calculators/DestinyMatrix/components/accordions/YearTasksAccordion.vue";
import PredispositionToDiseasesAccordion from "@/views/Calculators/DestinyMatrix/components/accordions/PredispositionToDiseasesAccordion.vue";

const props = defineProps<{
  matrix: DestinyMatrix | undefined;
  submittedDate: string;
  userHasPremiumAccess: boolean;
}>();

const waitApiCall = ref(false);
const showRequirePremiumModal = ref(false);

const modalDate = computed(() => {
  return props.submittedDate.replaceAll("-", ".");
});

const accordionStates = reactive({
  personal: false,
  family: false,
  realization: false,
  comfort: false,
  vacation: false,
  alcoholProne: false,
  childParentsKarma: false,
  familyTasksSevenGeneration: false,
  excessWeight: false,
  gettingReachByYear: false,
  karmaAndFortyYearsTasks: false,
  moneyInMatrix: false,
  personalPointOfPower: false,
  previousLifesTasks: false,
  purpose: false,
  relationsInMatrix: false,
  spiritKarma: false,
  whatGivesYouResource: false,
  whomWorksForSoul: false,
  healthMap: false,
  programs: false,
  yeasTasks: false,
  predispositionToDiseases: false,
});

watch(
  () => props.matrix,
  () => {
    closeAllAccordions();
  }
);

function closeAllAccordions() {
  Object.keys(accordionStates).forEach((key) => {
    accordionStates[key as keyof typeof accordionStates] = false;
  });
}

async function getDescription(key: DestinyMatrixKeysEnum) {
  if (PREMIUM_KEYS_SET.has(key) && !props.userHasPremiumAccess) {
    showRequirePremiumModal.value = true;
    return;
  }

  try {
    waitApiCall.value = true;
    const apiDate = props.submittedDate.split("-").reverse().join("-");
    return await getMatrixDescription(key, apiDate);
  } catch (error) {
    console.error(error);
  } finally {
    waitApiCall.value = false;
  }
}

provide("getDescription", getDescription);
</script>

<template>
  <div class="flex flex-col gap-2">
    <PersonalDataAccordion
      :matrix="matrix"
      v-model="accordionStates.personal"
    />

    <FamilyPowerAccordion :matrix="matrix" v-model="accordionStates.family" />

    <PointOfSpiritComfortAccordion
      :matrix="matrix"
      v-model="accordionStates.comfort"
    />

    <SelfRealizationAccordion
      :matrix="matrix"
      v-model="accordionStates.realization"
    />

    <VacationAccordion :matrix="matrix" v-model="accordionStates.vacation" />

    <YearTasksAccordion :matrix="matrix" v-model="accordionStates.yeasTasks" />

    <!-- PREMIUM CASES -->
    <AlcoholProneAccordion
      :matrix="matrix"
      :hasPremiumAccess="userHasPremiumAccess"
      v-model="accordionStates.alcoholProne"
    />

    <ChildParentsKarmaAccordion
      :matrix="matrix"
      :hasPremiumAccess="userHasPremiumAccess"
      v-model="accordionStates.childParentsKarma"
    />

    <FamilyTasksSevenGenerationAccordion
      :matrix="matrix"
      :hasPremiumAccess="userHasPremiumAccess"
      v-model="accordionStates.familyTasksSevenGeneration"
    />

    <ExcessWeightAccordion
      :matrix="matrix"
      :hasPremiumAccess="userHasPremiumAccess"
      v-model="accordionStates.excessWeight"
    />

    <GettingReachByBirthYearAccordion
      :matrix="matrix"
      :hasPremiumAccess="userHasPremiumAccess"
      v-model="accordionStates.gettingReachByYear"
    />

    <KarmaAndFortyYearsTasksAccordion
      :matrix="matrix"
      :hasPremiumAccess="userHasPremiumAccess"
      v-model="accordionStates.karmaAndFortyYearsTasks"
    />

    <MoneyInMatrixAccordion
      :matrix="matrix"
      :hasPremiumAccess="userHasPremiumAccess"
      v-model="accordionStates.moneyInMatrix"
    />

    <PersonalPointOfPowerAccordion
      :matrix="matrix"
      :hasPremiumAccess="userHasPremiumAccess"
      v-model="accordionStates.personalPointOfPower"
    />

    <PrevLifesTasksAccordion
      :matrix="matrix"
      :hasPremiumAccess="userHasPremiumAccess"
      v-model="accordionStates.previousLifesTasks"
    />

    <PurposesAccordion
      :matrix="matrix"
      :hasPremiumAccess="userHasPremiumAccess"
      v-model="accordionStates.purpose"
    />

    <RelationsInMatrixAccordion
      :matrix="matrix"
      :hasPremiumAccess="userHasPremiumAccess"
      v-model="accordionStates.relationsInMatrix"
    />

    <SpiritKarmaAccordion
      :matrix="matrix"
      :hasPremiumAccess="userHasPremiumAccess"
      v-model="accordionStates.spiritKarma"
    />

    <WhatGivesYouResourceAccordion
      :matrix="matrix"
      :hasPremiumAccess="userHasPremiumAccess"
      v-model="accordionStates.whatGivesYouResource"
    />

    <WhomWorksForSoulAccordion
      :matrix="matrix"
      :hasPremiumAccess="userHasPremiumAccess"
      v-model="accordionStates.whomWorksForSoul"
    />

    <HealthMapAccordion
      :matrix="matrix"
      :hasPremiumAccess="userHasPremiumAccess"
      v-model="accordionStates.healthMap"
    />

    <ProgramsAccordion
      :matrix="matrix"
      :hasPremiumAccess="userHasPremiumAccess"
      v-model="accordionStates.programs"
    />

    <PredispositionToDiseasesAccordion
      :matrix="matrix"
      :hasPremiumAccess="userHasPremiumAccess"
      v-model="accordionStates.predispositionToDiseases"
    />

    <EmptyModal :show="waitApiCall" />

    <RequirePremiumModal
      :show="showRequirePremiumModal"
      :selectedDate="modalDate"
      @close="showRequirePremiumModal = false"
    />
  </div>
</template>
